import { IntlShape, MessageDescriptor } from 'react-intl'

import { RequireEligibilityConfirmationLDFlag } from './types'
import {
  CustomerCopy,
  EligibilityStatus,
  RadioButtonType,
  TransferAccountInfoResponse,
  YearProgramCoverage,
} from '../../models'
import { EligibilityInfo, EligibilityType } from '../common/constants/constants'
import { CustomerName } from '../common/constants/customer'
import {
  COMMON_MIXPANEL_PROPERTIES,
  ELIGIBILITY_PROPERTIES,
  ELIGIBILITY_PROPERTY_VALUE,
  REGISTRATION_EVENTS,
} from '../common/constants/mixpanelConstants'
import { areAllCustomerProgramsBHBExclusive } from '../healthPlan/utils'
import { RegistrationType } from '../registration/types'

export interface EligibilityCheckParams {
  customerPrograms?: YearProgramCoverage
  hasBlockingEligibilityCheck: boolean
  isCustomerOptedOutOfEligibilityCheck: boolean
  approveClaimsWithNoEligibilityCheck: boolean
  isInternationalRegistrationExperience: boolean
  hideDependentField?: boolean
  registrationType?: RegistrationType
  customerName: string
}

export const skipEligibilityCheck = ({
  customerPrograms,
  hasBlockingEligibilityCheck,
  isCustomerOptedOutOfEligibilityCheck,
  approveClaimsWithNoEligibilityCheck,
  isInternationalRegistrationExperience,
  hideDependentField,
  registrationType,
  customerName,
}: EligibilityCheckParams) => {
  const isCustomerHPIExclusive = areAllCustomerProgramsBHBExclusive(customerPrograms)
  /*
    Users will automatically skip an eligibility check for the following cases:
    1. A self-managed teen is completing the parent-led registration
    2. A self-managed teen is completing the family hub registration
    where eligibility is inherited from their parent/guardian.
    3. Auto-enrollment, where the registration user info is sourced directly from eligibility data
   */
  if (
    registrationType === 'PARENT_LED_TEEN' ||
    registrationType === 'FAMILY_HUB' ||
    registrationType === 'AUTO_ENROLLMENT'
  ) {
    return true
  }

  if (hasBlockingEligibilityCheck) {
    return false
  }

  let skipCheck =
    isCustomerOptedOutOfEligibilityCheck ||
    isCustomerHPIExclusive ||
    isInternationalRegistrationExperience ||
    hideDependentField
  /*
    Users will skip an eligibility check if any of the following are true:
    1. The customer does not require a blocking eligibility check for registration or claims if not apple
    2. The customer has opted out of an eligibility check, processes claims without an eligibility check, is HPI-only, or
       is international and an international country is selected
  */
  // TODO: Temporary check for apple until https://lyrahealth.atlassian.net/browse/ACCOUNT-2764 is complete to determine if approveClaimsWithNoEligibilityCheck can be removed
  if (customerName !== CustomerName.APPLE) {
    skipCheck = skipCheck || approveClaimsWithNoEligibilityCheck
  }
  return skipCheck
}

export function createEligibilityCheckEventData({
  page,
  eligibilityStatus,
  hasBlockingEligibilityCheck,
  eligibilityType,
  attemptCount,
  pageEntryPoint,
}: {
  page: string
  eligibilityStatus: EligibilityStatus | undefined
  hasBlockingEligibilityCheck: boolean | undefined
  eligibilityType: EligibilityType | undefined
  attemptCount?: number
  pageEntryPoint?: string
}): { event: string; props?: Record<string, any> } {
  const sharedProps = {
    [COMMON_MIXPANEL_PROPERTIES.PAGE]: page,
    [ELIGIBILITY_PROPERTIES.ELIGIBILITY_TYPE]: eligibilityType,
    ...(pageEntryPoint && { [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: pageEntryPoint }),
  }

  if (eligibilityStatus === EligibilityStatus.ELIGIBLE) {
    return {
      event: hasBlockingEligibilityCheck
        ? REGISTRATION_EVENTS.EAP_HARD_ELIGIBILITY_CHECK_SUCCESS
        : REGISTRATION_EVENTS.EAP_SOFT_ELIGIBILITY_CHECK_SUCCESS,
      props: {
        ...sharedProps,
        [COMMON_MIXPANEL_PROPERTIES.ATTEMPT_COUNT]: attemptCount,
      },
    }
  } else {
    return {
      event: hasBlockingEligibilityCheck
        ? REGISTRATION_EVENTS.EAP_HARD_ELIGIBILITY_CHECK_ERROR
        : REGISTRATION_EVENTS.EAP_SOFT_ELIGIBILITY_CHECK_ERROR,
      props: {
        ...sharedProps,
        [ELIGIBILITY_PROPERTIES.ELIGIBILITY_ERROR_CAUSE]:
          eligibilityStatus === EligibilityStatus.INELIGIBLE
            ? ELIGIBILITY_PROPERTY_VALUE.ELIGIBILITY_ERROR_CAUSE_INELIGIBILITY
            : ELIGIBILITY_PROPERTY_VALUE.ELIGIBILITY_ERROR_CAUSE_UNEXPECTED_ERROR,
      },
    }
  }
}

export function shouldRequireEligibilityConfirmation(
  eligibilityStatus: EligibilityStatus | undefined,
  requireEligibilityConfirmation: RequireEligibilityConfirmationLDFlag | undefined,
  userFriendlyLyraCode?: string | null,
): boolean {
  return (
    requireEligibilityConfirmation?.isEnabled === true &&
    eligibilityStatus === EligibilityStatus.INELIGIBLE &&
    // skip eligibility confirmation for users assigned with lyra code
    (!requireEligibilityConfirmation.skipIneligibleUserWithLyraCode || !userFriendlyLyraCode)
  )
}

export function generateEligibilityQuestionTitleHelper(
  formatMessage: IntlShape['formatMessage'],
  title?: MessageDescriptor | string,
  values?: { employeeType: string; hasCustomerName: string; customerName: string },
) {
  if (typeof title === 'string') {
    return title
  }
  if (!title || !values) {
    return ''
  }

  return formatMessage(title, values)
}

export const generateEligibilityInfoForTransferAccount = (
  eligibility: EligibilityStatus | undefined,
  customerName: string,
  transferAccountInfo: TransferAccountInfoResponse,
  customerCopy: CustomerCopy,
  formatMessage: IntlShape['formatMessage'],
): EligibilityInfo => {
  const isUnexpectedEligibilityStatus = eligibility === EligibilityStatus.UNKNOWN

  const { dependentTypes, customerPropertiesMap, customerInfo } = transferAccountInfo

  const {
    employeeRegistrationNomenclature,
    dependentRegistrationNomenclature,
    hideDependentField,
    hasBlockingEligibilityCheck: hasBlockingEligibilityCheckProp,
    checkAdultDependentEligibilityUsingDependentInfo,
  } = customerPropertiesMap

  const {
    generateEmployeeEligibilityError,
    generateEligibilityQuestionTitle,
    generateEligibilityDependentTitle,
    eligibilityMetadata,
    dependentButtonText,
    dependentIneligibleText,
    showEligibilityQuestionForInternationalRegistration,
  } = customerCopy

  const employeeEligibilityError = generateEmployeeEligibilityError?.(true, isUnexpectedEligibilityStatus)

  const eligibilityTitleValues = {
    employeeType: employeeRegistrationNomenclature || 'other',
    hasCustomerName: customerInfo ? 'yes' : 'other',
    customerName: customerInfo,
  }

  const eligibilityQuestionTitle = generateEligibilityQuestionTitleHelper(
    formatMessage,
    generateEligibilityQuestionTitle?.(),
    eligibilityTitleValues,
  )

  const eligibilityDependentTitle = generateEligibilityQuestionTitleHelper(
    formatMessage,
    generateEligibilityDependentTitle?.(),
    eligibilityTitleValues,
  )

  const hasBlockingEligibilityCheck = hasBlockingEligibilityCheckProp === 'true' || false

  return {
    eligibilityQuestionTitle,
    eligibilityDependentTitle,
    customerName,
    hideDependentField: hideDependentField === 'true' || false,
    employeeRegistrationNomenclature,
    dependentRegistrationNomenclature,
    dependentTypes,
    customEligibilityMetadata: eligibilityMetadata,
    showRegistrationTips:
      hasBlockingEligibilityCheck && (eligibility === EligibilityStatus.INELIGIBLE || isUnexpectedEligibilityStatus),
    hasBlockingEligibilityCheck,
    checkAdultDependentEligibilityUsingDependentInfo:
      checkAdultDependentEligibilityUsingDependentInfo === 'true' || false,
    employeeEligibilityError,
    isPristineConfirmEligibility: true,
    dependentButtonText,
    dependentIneligibleText,
    showEligibilityQuestionForInternationalRegistration,
    ...(!hasBlockingEligibilityCheck && { eligibilityRadioButtonType: RadioButtonType.CIRCLE_TEXT_OUTLINE }),
  }
}
